// src/components/seo/SeoContent.tsx
import { useLocalization } from '@/hooks/useLocalization';
import LocalizedContent from './LocalizedContent';
import { SupportedLanguage } from '@/config/routes';

const translations = {
  useCases: {
    en: 'Use Cases',
    es: 'Casos de Uso',
    fr: 'Cas d\'Utilisation',
    de: 'Anwendungsfälle',
    it: 'Casi d\'Uso',
    pl: 'Przypadki Użycia',
    pt: 'Casos de Uso',
    'pt-BR': 'Casos de Uso',
    nl: 'Gebruikstoepassingen',
    ja: '使用例'
  },
  howToUse: {
    en: 'How to Use',
    es: 'Cómo Usar',
    fr: 'Mode d\'Emploi',
    de: 'Verwendung',
    it: 'Come Usare',
    pl: 'Jak Używać',
    pt: 'Como Usar',
    'pt-BR': 'Como Usar',
    nl: 'Gebruiksaanwijzing',
    ja: '使用方法'
  },
  features: {
    en: 'Features',
    es: 'Características',
    fr: 'Fonctionnalités',
    de: 'Funktionen',
    it: 'Funzionalità',
    pl: 'Funkcje',
    pt: 'Recursos',
    'pt-BR': 'Recursos',
    nl: 'Functies',
    ja: '機能'
  },
  whyChoose: {
    en: 'Why Choose Our Tool',
    es: '¿Por Qué Elegirnos?',
    fr: 'Pourquoi Choisir Notre Outil',
    de: 'Warum Unser Tool',
    it: 'Perché Scegliere il Nostro Strumento',
    pl: 'Dlaczego Wybrać Nasze Narzędzie',
    pt: 'Por Que Escolher Nossa Ferramenta',
    'pt-BR': 'Por Que Escolher Nossa Ferramenta',
    nl: 'Waarom Onze Tool Kiezen',
    ja: '当ツールを選ぶ理由'
  }
} as const;

const useCases: Record<SupportedLanguage, string[]> = {
  en: [
    'Light source for zoom calls and video conferences',
    'Test monitor display and color calibration',
    'Create a distraction-free environment',
    'Use as a background for drawings and tracing',
    'Make flipbooks and animations',
    'Focus tool for studying or working'
  ],
  es: [
    'Fuente de luz para llamadas de Zoom y videoconferencias',
    'Prueba de pantalla y calibración de color',
    'Crea un entorno libre de distracciones',
    'Usa como fondo para dibujos y calcos',
    'Hacer flipbooks y animaciones',
    'Herramienta de enfoque para estudiar o trabajar'
  ],
  fr: [
    'Source de lumière pour les appels Zoom et les vidéoconférences',
    'Test d\'écran et calibrage des couleurs',
    'Créez un environnement sans distraction',
    'Utiliser comme fond pour les dessins et le traçage',
    'Faire des flipbooks et des animations',
    'Outil de concentration pour étudier ou travailler'
  ],
  de: [
    'Lichtquelle für Zoom-Anrufe und Videokonferenzen',
    'Bildschirmtest und Farbkalibrierung',
    'Schaffen Sie eine ablenkungsfreie Umgebung',
    'Als Hintergrund für Zeichnungen und Durchpausen verwenden',
    'Daumenkinos und Animationen erstellen',
    'Fokus-Tool zum Lernen oder Arbeiten'
  ],
  it: [
    'Fonte di luce per chiamate Zoom e videoconferenze',
    'Test del monitor e calibrazione del colore',
    'Crea un ambiente senza distrazioni',
    'Usa come sfondo per disegni e tracciature',
    'Creare flipbook e animazioni',
    'Strumento di concentrazione per studiare o lavorare'
  ],
  pl: [
    'Źródło światła do rozmów Zoom i wideokonferencji',
    'Test monitora i kalibracja kolorów',
    'Stwórz środowisko bez rozpraszania uwagi',
    'Używaj jako tła do rysunków i odrysowywania',
    'Twórz flipbooki i animacje',
    'Narzędzie do skupienia się na nauce lub pracy'
  ],
  pt: [
    'Fonte de luz para chamadas Zoom e videoconferências',
    'Teste de monitor e calibração de cores',
    'Crie um ambiente livre de distrações',
    'Use como fundo para desenhos e traçados',
    'Faça flipbooks e animações',
    'Ferramenta de foco para estudar ou trabalhar'
  ],
  'pt-BR': [
    'Fonte de luz para chamadas Zoom e videoconferências',
    'Teste de monitor e calibração de cores',
    'Crie um ambiente livre de distrações',
    'Use como fundo para desenhos e traçados',
    'Faça flipbooks e animações',
    'Ferramenta de foco para estudar ou trabalhar'
  ],
  nl: [
    'Lichtbron voor Zoom-gesprekken en videoconferenties',
    'Monitortest en kleurkalibratie',
    'Creëer een afleidingsvrije omgeving',
    'Gebruik als achtergrond voor tekeningen en overtrekken',
    'Maak flipbooks en animaties',
    'Focustool voor studeren of werken'
  ],
  ja: [
    'Zoomコールやビデオ会議の光源として',
    'モニターテストと色調整',
    '集中できる環境を作る',
    '描画やトレースの背景として使用',
    'フリップブックとアニメーションの作成',
    '学習や作業に集中するためのツール'
  ]
};

const SeoContent = () => {
  const { currentLang } = useLocalization();

  return (
    <div className="max-w-4xl mx-auto mt-16 space-y-12 text-left px-6">
      <section className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">
          <LocalizedContent translations={translations.useCases} />
        </h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">
          <ul className="list-disc list-inside space-y-2">
            {useCases[currentLang].map((useCase, index) => (
              <li key={index} className="text-lg">{useCase}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">
          <LocalizedContent translations={translations.howToUse} />
        </h2>
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground">
            <LocalizedContent
              translations={{
                en: 'Simply select your desired color from the palette or enter a custom color code. Click the fullscreen button to display the color in fullscreen mode. Exit fullscreen by pressing ESC (Windows) or Command+Control+F (Mac), or use the minimize button.',
                es: 'Simplemente selecciona el color deseado de la paleta o ingresa un código de color personalizado. Haz clic en el botón de pantalla completa para mostrar el color en modo pantalla completa. Sal del modo pantalla completa presionando ESC (Windows) o Comando+Control+F (Mac), o usa el botón minimizar.',
                fr: 'Sélectionnez simplement la couleur souhaitée dans la palette ou entrez un code de couleur personnalisé. Cliquez sur le bouton plein écran pour afficher la couleur en mode plein écran. Quittez le plein écran en appuyant sur ESC (Windows) ou Commande+Control+F (Mac), ou utilisez le bouton minimiser.',
                de: 'Wählen Sie einfach die gewünschte Farbe aus der Palette oder geben Sie einen benutzerdefinierten Farbcode ein. Klicken Sie auf die Vollbildschaltfläche, um die Farbe im Vollbildmodus anzuzeigen. Beenden Sie den Vollbildmodus durch Drücken von ESC (Windows) oder Befehl+Strg+F (Mac), oder verwenden Sie die Minimieren-Schaltfläche.',
                it: 'Seleziona semplicemente il colore desiderato dalla tavolozza o inserisci un codice colore personalizzato. Fai clic sul pulsante a schermo intero per visualizzare il colore in modalità schermo intero. Esci dalla modalità schermo intero premendo ESC (Windows) o Comando+Control+F (Mac), o usa il pulsante minimizza.',
                pl: 'Po prostu wybierz żądany kolor z palety lub wprowadź niestandardowy kod koloru. Kliknij przycisk pełnego ekranu, aby wyświetlić kolor w trybie pełnoekranowym. Wyjdź z trybu pełnoekranowego, naciskając ESC (Windows) lub Command+Control+F (Mac), lub użyj przycisku minimalizacji.',
                pt: 'Simplesmente selecione a cor desejada da paleta ou insira um código de cor personalizado. Clique no botão de tela cheia para exibir a cor no modo de tela cheia. Saia da tela cheia pressionando ESC (Windows) ou Command+Control+F (Mac), ou use o botão minimizar.',
                'pt-BR': 'Simplesmente selecione a cor desejada da paleta ou insira um código de cor personalizado. Clique no botão de tela cheia para exibir a cor no modo de tela cheia. Saia da tela cheia pressionando ESC (Windows) ou Command+Control+F (Mac), ou use o botão minimizar.',
                nl: 'Selecteer eenvoudig de gewenste kleur uit het palet of voer een aangepaste kleurcode in. Klik op de volledig scherm-knop om de kleur in volledig scherm weer te geven. Verlaat volledig scherm door op ESC (Windows) of Command+Control+F (Mac) te drukken, of gebruik de minimaliseer-knop.',
                ja: 'パレットから希望の色を選択するか、カスタムカラーコードを入力してください。フルスクリーンボタンをクリックすると、色がフルスクリーンモードで表示されます。フルスクリーンを終了するには、ESC（Windows）またはCommand+Control+F（Mac）を押すか、最小化ボタンを使用してください。'
              }}
            />
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">
          <LocalizedContent translations={translations.features} />
        </h2>
        <ul className="grid sm:grid-cols-2 gap-6 text-muted-foreground">
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Multiple color options for various needs',
                es: '• Múltiples opciones de color para diversas necesidades',
                fr: '• Plusieurs options de couleur pour différents besoins',
                de: '• Mehrere Farboptionen für verschiedene Bedürfnisse',
                it: '• Molteplici opzioni di colore per varie esigenze',
                pl: '• Wiele opcji kolorów dla różnych potrzeb',
                pt: '• Múltiplas opções de cores para várias necessidades',
                'pt-BR': '• Múltiplas opções de cores para várias necessidades',
                nl: '• Meerdere kleuropties voor verschillende behoeften',
                ja: '• さまざまなニーズに対応する複数の色オプション'
              }}
            />
          </li>
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Custom color support with hex codes',
                es: '• Soporte de colores personalizados con códigos hex',
                fr: '• Support de couleurs personnalisées avec codes hex',
                de: '• Benutzerdefinierte Farbunterstützung mit Hex-Codes',
                it: '• Supporto colori personalizzati con codici hex',
                pl: '• Obsługa niestandardowych kolorów z kodami hex',
                pt: '• Suporte a cores personalizadas com códigos hex',
                'pt-BR': '• Suporte a cores personalizadas com códigos hex',
                nl: '• Aangepaste kleurondersteuning met hex-codes',
                ja: '• 16進数コードによるカスタムカラーのサポート'
              }}
            />
          </li>
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Quick keyboard shortcuts',
                es: '• Atajos de teclado rápidos',
                fr: '• Raccourcis clavier rapides',
                de: '• Schnelle Tastaturkürzel',
                it: '• Scorciatoie da tastiera rapide',
                pl: '• Szybkie skróty klawiaturowe',
                pt: '• Atalhos de teclado rápidos',
                'pt-BR': '• Atalhos de teclado rápidos',
                nl: '• Snelle toetsenbordsnelkoppelingen',
                ja: '• クイックキーボードショートカット'
              }}
            />
          </li>
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Easy fullscreen toggle',
                es: '• Fácil alternancia de pantalla completa',
                fr: '• Basculement facile en plein écran',
                de: '• Einfache Vollbild-Umschaltung',
                it: '• Facile attivazione schermo intero',
                pl: '• Łatwe przełączanie pełnego ekranu',
                pt: '• Alternância fácil de tela cheia',
                'pt-BR': '• Alternância fácil de tela cheia',
                nl: '• Eenvoudige volledig scherm-schakelaar',
                ja: '• 簡単なフルスクリーン切り替え'
              }}
            />
          </li>
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Multiple resolution options',
                es: '• Múltiples opciones de resolución',
                fr: '• Plusieurs options de résolution',
                de: '• Mehrere Auflösungsoptionen',
                it: '• Opzioni multiple di risoluzione',
                pl: '• Wiele opcji rozdzielczości',
                pt: '• Múltiplas opções de resolução',
                'pt-BR': '• Múltiplas opções de resolução',
                nl: '• Meerdere resolutieopties',
                ja: '• 複数の解像度オプション'
              }}
            />
          </li>
          <li className="flex items-start gap-3 text-lg">
            <LocalizedContent
              translations={{
                en: '• Background image download',
                es: '• Descarga de imagen de fondo',
                fr: '• Téléchargement d\'image d\'arrière-plan',
                de: '• Hintergrundbild-Download',
                it: '• Download immagine di sfondo',
                pl: '• Pobieranie obrazu tła',
                pt: '• Download de imagem de fundo',
                'pt-BR': '• Download de imagem de fundo',
                nl: '• Achtergrondafbeelding downloaden',
                ja: '• 背景画像のダウンロード'
              }}
            />
          </li>
        </ul>
      </section>

      <section className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">
          <LocalizedContent translations={translations.whyChoose} />
        </h2>
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground">
            <LocalizedContent
              translations={{
                en: 'Our online screen tool is fast, free, and requires no installation. Perfect for professionals, students, and anyone needing a quick full-screen color display. Works on all modern browsers and devices.',
                es: 'Nuestra herramienta de pantalla en línea es rápida, gratuita y no requiere instalación. Perfecta para profesionales, estudiantes y cualquier persona que necesite una pantalla de color a pantalla completa rápida. Funciona en todos los navegadores y dispositivos modernos.',
                fr: 'Notre outil d\'écran en ligne est rapide, gratuit et ne nécessite aucune installation. Parfait pour les professionnels, les étudiants et toute personne ayant besoin d\'un affichage couleur plein écran rapide. Fonctionne sur tous les navigateurs et appareils modernes.',
                de: 'Unser Online-Bildschirm-Tool ist schnell, kostenlos und erfordert keine Installation. Perfekt für Berufstätige, Studenten und alle, die eine schnelle Vollbildfarbanzeige benötigen. Funktioniert auf allen modernen Browsern und Geräten.',
                it: 'Il nostro strumento schermo online è veloce, gratuito e non richiede installazione. Perfetto per professionisti, studenti e chiunque abbia bisogno di una visualizzazione rapida a colori a schermo intero. Funziona su tutti i browser e dispositivi moderni.',
                pl: 'Nasze narzędzie ekranu online jest szybkie, bezpłatne i nie wymaga instalacji. Idealne dla profesjonalistów, studentów i wszystkich potrzebujących szybkiego wyświetlania kolorów na pełnym ekranie. Działa na wszystkich nowoczesnych przeglądarkach i urządzeniach.',
                pt: 'Nossa ferramenta de tela online é rápida, gratuita e não requer instalação. Perfeita para profissionais, estudantes e qualquer pessoa que precise de uma exibição rápida de cor em tela cheia. Funciona em todos os navegadores e dispositivos modernos.',
                'pt-BR': 'Nossa ferramenta de tela online é rápida, gratuita e não requer instalação. Perfeita para profissionais, estudantes e qualquer pessoa que precise de uma exibição rápida de cor em tela cheia. Funciona em todos os navegadores e dispositivos modernos.',
                nl: 'Onze online schermtool is snel, gratis en vereist geen installatie. Perfect voor professionals, studenten en iedereen die een snelle volledig scherm kleurenweergave nodig heeft. Werkt op alle moderne browsers en apparaten.',
                ja: '当社のオンライン画面ツールは、高速で無料で、インストールも不要です。プロフェッショナル、学生、そして素早いフルスクリーンカラー表示を必要とするすべての人に最適です。すべての最新のブラウザとデバイスで動作します。'
              }}
            />
          </p>
        </div>
      </section>
    </div>
  );
};

export default SeoContent;